import React from "react";
import LockOutlined from "@mui/icons-material/LockOutlined";
import { useForm, useFormState } from "react-hook-form";
import { DEFAULT_PASSWORD, passwordValidationRules } from "./validations";
import { PasswordFormData, PasswordFormProps } from "./types";
import { FormInput } from "../../components/Input";
import { PasswordStrength } from "./components";
import { DiscardPopUp } from "../../components/PopUp";
import { SaveBar } from "./components/SaveBar";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  IconContainer,
  TitleRow,
  FormGrid,
  FormGroup,
  Label,
  HelperText,
} from "./styles";

export const PasswordFormComponent: React.FC<PasswordFormProps> = ({
  onUpdate,
  onFormDirtyChange,
  saveFormRef,
  cancelFormRef,
}) => {
  const [showConfirm, setShowConfirm] = React.useState(false);

  const passwordForm = useForm<PasswordFormData>({
    defaultValues: DEFAULT_PASSWORD,
    mode: "onBlur", // Validate on blur (first touch)
    reValidateMode: "onChange", // Re-validate in real-time after first blur
  });

  const { isDirty, isSubmitting } = useFormState({
    control: passwordForm.control,
  });
  const newPassword = passwordForm.watch("newPassword");

  const rules = passwordValidationRules(passwordForm);

  // Notify parent of dirty state changes
  React.useEffect(() => {
    onFormDirtyChange?.(isDirty);
  }, [isDirty, onFormDirtyChange]);

  const onSubmit = React.useCallback(
    async (data: PasswordFormData) => {
      const success = await onUpdate(data);
      if (success) {
        passwordForm.reset(DEFAULT_PASSWORD);
      }
    },
    [onUpdate, passwordForm],
  );

  const handleRevert = React.useCallback(() => {
    setShowConfirm(true);
  }, []);

  const confirmRevert = React.useCallback(() => {
    passwordForm.reset(DEFAULT_PASSWORD);
    setShowConfirm(false);
  }, [passwordForm]);

  // Expose save and cancel methods to parent via refs
  React.useEffect(() => {
    if (saveFormRef) {
      saveFormRef.current = () => {
        if (isDirty) {
          passwordForm.handleSubmit(onSubmit)();
        }
      };
    }
    if (cancelFormRef) {
      cancelFormRef.current = () => {
        if (isDirty) {
          handleRevert();
        }
      };
    }
  }, [
    isDirty,
    passwordForm,
    onSubmit,
    handleRevert,
    saveFormRef,
    cancelFormRef,
  ]);

  return (
    <Card className="security">
      <CardHeader>
        <TitleRow>
          <IconContainer>
            <LockOutlined />
          </IconContainer>
          <CardTitle>Security</CardTitle>
        </TitleRow>
        <CardDescription>
          Keep your account secure by using a strong, unique password
        </CardDescription>
      </CardHeader>
      <form onSubmit={passwordForm.handleSubmit(onSubmit)}>
        <FormGrid>
          <FormGroup className="full-width">
            <Label>
              Current Password<span className="required">*</span>
            </Label>
            <FormInput
              type="password"
              name="currentPassword"
              control={passwordForm.control}
              rules={rules.currentPassword}
              disabled={isSubmitting}
              placeholder="Enter your current password"
              autoComplete="current-password"
            />
            <HelperText>
              Enter your current password to authorize changes
            </HelperText>
          </FormGroup>
          <FormGroup className="full-width">
            <Label>
              New Password<span className="required">*</span>
            </Label>
            <FormInput
              type="password"
              name="newPassword"
              control={passwordForm.control}
              rules={rules.newPassword}
              disabled={isSubmitting}
              placeholder="Enter your new password"
              autoComplete="new-password"
            />
            <PasswordStrength password={newPassword || ""} />
          </FormGroup>
          <FormGroup className="full-width">
            <Label>
              Confirm New Password<span className="required">*</span>
            </Label>
            <FormInput
              type="password"
              name="confirmPassword"
              control={passwordForm.control}
              rules={rules.confirmPassword}
              disabled={isSubmitting}
              placeholder="Re-enter your new password"
              autoComplete="new-password"
            />
            <HelperText>Must match your new password</HelperText>
          </FormGroup>
        </FormGrid>
      </form>

      <SaveBar
        isDirty={isDirty}
        isSubmitting={isSubmitting}
        onSave={passwordForm.handleSubmit(onSubmit)}
        onDiscard={handleRevert}
      />

      <DiscardPopUp
        isOpen={showConfirm}
        setIsOpen={setShowConfirm}
        eyebrow="CHANGING PASSWORD"
        title="Discard your changes?"
        text="The password you started entering hasn't been saved yet. If you leave now they'll be lost."
        handleOnDiscard={confirmRevert}
      />
    </Card>
  );
};

export const PasswordForm = React.memo(PasswordFormComponent);
